import { useId } from 'react';
import { Link } from 'react-router';
import type { HubGroup, HubRow } from './hubRows';
import { IconBadge } from './icons';

/**
 * Zeile eines Basar-Bereichs (Plan 16, U3b): Symbol, Titel, darunter die Live-Zahl oder, ohne Zahl, der feste Zweck.
 * Gesperrte Zeilen bleiben sichtbar, sind kein Link (`aria-disabled`) und nennen den Grund als Text, nie nur als Grau.
 */
export function AreaRow({ row }: { row: HubRow }) {
  const body = (
    <>
      <IconBadge name={row.icon} />
      <span className="area-row__text">
        <span className="area-row__title">{row.title}</span>{' '}
        {row.locked === undefined ? (
          <span
            className={
              row.status === undefined
                ? 'area-row__meta area-row__meta--hint'
                : 'area-row__meta area-row__meta--live'
            }
          >
            {row.status ?? row.description}
          </span>
        ) : (
          <span className="area-row__meta area-row__reason">{row.locked}</span>
        )}
      </span>
      {row.badge !== undefined && <span className="badge area-row__badge">{row.badge}</span>}
      {row.locked === undefined && (
        <span className="area-row__chevron" aria-hidden="true">
          ›
        </span>
      )}
    </>
  );
  if (row.locked !== undefined) {
    return (
      <div className="area-row area-row--locked" aria-disabled="true">
        {body}
      </div>
    );
  }
  return (
    <Link className="area-row" to={row.to}>
      {body}
    </Link>
  );
}

/** Gruppe mit Überschrift (Vorbereitung, Basartag, …) und Zeilen als Liste; vergangene Phasen eingeklappt. */
export function AreaGroup({ group }: { group: HubGroup }) {
  const id = useId();
  if (group.rows.length === 0) return null;
  // Eingeklappt gehen offene Punkte („3 offen“) nicht verloren: Die Zeile nennt, wie viele Zeilen einen Hinweis tragen
  const flagged = group.rows.filter((row) => row.badge !== undefined).length;
  const list = (
    <ul className="area-list">
      {group.rows.map((row) => (
        <li key={row.key}>
          <AreaRow row={row} />
        </li>
      ))}
    </ul>
  );
  if (group.collapsed === true) {
    return (
      <section className="area-group area-group--collapsed" aria-labelledby={id}>
        <details>
          <summary className="area-group__summary">
            <h2 id={id} className="area-group__title">
              {group.title}
            </h2>
            <span className="area-group__count">
              ({group.rows.length}
              {flagged > 0 ? `, ${String(flagged)} mit Hinweis` : ''})
            </span>
          </summary>
          {list}
        </details>
      </section>
    );
  }
  return (
    <section className="area-group" aria-labelledby={id}>
      <h2 id={id} className="area-group__title">
        {group.title}
      </h2>
      {list}
    </section>
  );
}
